// =============================================================================
// IMPORTANT: When adding new VITE_ variables here, also add them to:
// - .github/workflows/deploy.yml (both staging and production env sections)
// =============================================================================

import { createEnv } from "@t3-oss/env-core"
import type { Address } from "viem"
import { z } from "zod"

// Ethereum address validator - zod validates the format, then we cast to Address
// This is safe because zod guarantees the regex matches the Address pattern
const addressSchema = z
	.string()
	.regex(/^0x[a-fA-F0-9]{40}$/, "Invalid Ethereum address") as z.ZodType<Address>

const optionalAddressSchema = addressSchema.optional()

export const env = createEnv({
	/**
	 * Client-side environment variables (available in browser).
	 * Must be prefixed with VITE_ to be exposed by Vite.
	 */
	clientPrefix: "VITE_",
	client: {
		// Required
		VITE_CONVEX_URL: z.string().url().optional(),
		VITE_PRIVY_APP_ID: z.string().min(1),

		// Optional with type validation
		VITE_CHAIN_ID: z.enum(["8453", "84532", "31337"]).optional(),
		VITE_RPC_URL: z.string().url().optional(),

		// Solana RPC for cross-chain bridging (optional)
		VITE_SOLANA_RPC_URL: z.string().url().optional(),

		// Sentry (optional - disabled if not set)
		VITE_SENTRY_DSN: z.string().url().optional(),
		VITE_SENTRY_DEBUG: z
			.string()
			.optional()
			.transform(val => val === "true"),

		// Deployment environment
		VITE_DEPLOY_ENVIRONMENT: z.enum(["development", "preview", "production"]).optional(),

		// E2E test variables
		VITE_E2E_TEST_MARKET_ADDRESS: optionalAddressSchema,
		VITE_E2E_USDC_ADDRESS: optionalAddressSchema,
	},

	/**
	 * Runtime environment for Vite
	 */
	runtimeEnv: {
		VITE_CONVEX_URL: import.meta.env.VITE_CONVEX_URL,
		VITE_PRIVY_APP_ID: import.meta.env.VITE_PRIVY_APP_ID,
		VITE_CHAIN_ID: import.meta.env.VITE_CHAIN_ID,
		VITE_RPC_URL: import.meta.env.VITE_RPC_URL,
		VITE_SOLANA_RPC_URL: import.meta.env.VITE_SOLANA_RPC_URL,
		VITE_SENTRY_DSN: import.meta.env.VITE_SENTRY_DSN,
		VITE_SENTRY_DEBUG: import.meta.env.VITE_SENTRY_DEBUG,
		VITE_DEPLOY_ENVIRONMENT: import.meta.env.VITE_DEPLOY_ENVIRONMENT,
		VITE_E2E_TEST_MARKET_ADDRESS: import.meta.env.VITE_E2E_TEST_MARKET_ADDRESS,
		VITE_E2E_USDC_ADDRESS: import.meta.env.VITE_E2E_USDC_ADDRESS,
	},

	/**
	 * Skip validation in certain environments
	 */
	skipValidation: false,

	/**
	 * Empty string handling - treat as undefined
	 */
	emptyStringAsUndefined: true,
})

// =============================================================================
// Backward-compatible exports (same names as before)
// =============================================================================

export const VITE_CONVEX_URL = env.VITE_CONVEX_URL
export const VITE_PRIVY_APP_ID = env.VITE_PRIVY_APP_ID
export const VITE_CHAIN_ID = env.VITE_CHAIN_ID
export const VITE_RPC_URL = env.VITE_RPC_URL
export const VITE_SOLANA_RPC_URL = env.VITE_SOLANA_RPC_URL
export const VITE_SENTRY_DSN = env.VITE_SENTRY_DSN
export const VITE_SENTRY_DEBUG = env.VITE_SENTRY_DEBUG
export const VITE_DEPLOY_ENVIRONMENT = env.VITE_DEPLOY_ENVIRONMENT

// E2E variables - MODE is "e2e" when running `vite --mode e2e`
export const E2E_MODE = import.meta.env.MODE === "e2e"
export const E2E_TEST_MARKET_ADDRESS = env.VITE_E2E_TEST_MARKET_ADDRESS
export const E2E_USDC_ADDRESS = env.VITE_E2E_USDC_ADDRESS
